/* eslint-disable @dropbox/sign/dig-form-label-html-for */
import {
  FormLabel,
  FormLabelProps,
  FormRow,
} from '@dropbox/dig-components/form_row';
import { Text, Title } from '@dropbox/dig-components/typography';
import { Box, Split } from '@dropbox/dig-foundations';
import classNames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';
import {
  signatureRequestContext,
  useFieldsContext,
} from 'signer-app/signature-request/context';
import { useSignerContext } from 'signer-app/signer-experience/signer-experience';
import {
  getGroupValidationInfo,
  isGroupRequired,
} from 'js/sign-components/common/group-requirement';
import { useTouchedFieldIds } from 'signer-app/signer-experience/use-validation';
import styles from 'signer-app/signer-signature-document/accessible-document.module.css';
import { AttachmentsField } from 'signer-app/signer-signature-document/field-components/attachments-field';
import { FieldHelperText } from 'signer-app/signer-signature-document/field-components/field-helper-text';
import { FieldInput } from 'signer-app/signer-signature-document/field-input';
import { messages } from 'signer-app/signer-signature-document/messages';
import { Field, FieldTypes } from 'signer-app/types/editor-types';
import { WhiteLabelThemeProvider } from 'signer-app/white-labeling/theme-provider';

function FieldLabel({
  field,
  variant,
}: {
  field: Field;
  variant?: FormLabelProps['variant'];
}) {
  return (
    <FormLabel htmlFor={`field_${field.id}`} variant={variant}>
      {field.label ??
        field.formLabel ??
        field.name ??
        field.autoFillType ??
        field.id}
    </FormLabel>
  );
}

type GroupProps = {
  group: Field[];
  label?: string;
  validationError?: string;
  validationInfo?: string;
  groupId: string;
  required: boolean;
};

function FieldGroup({
  label,
  group,
  groupId,
  required,
  validationError,
  validationInfo,
}: GroupProps) {
  return (
    <div
      role="group"
      aria-describedby={`validation_${groupId}`}
      className={styles.fieldGroup}
    >
      <FormLabel tagName="legend">{label}</FormLabel>
      {validationInfo && (
        <Box marginBottom={'Micro Small'}>
          <Text color="subtle" size="medium">
            {validationInfo}
          </Text>
        </Box>
      )}
      <Box className={classNames(styles.scroll, styles.checkboxGroup)}>
        {group.map((field) => (
          <FormRow key={field.id} variant="control">
            <FieldInput field={field} />
            <FieldLabel field={field} variant="control" />
          </FormRow>
        ))}
      </Box>
      <FieldHelperText
        id={groupId}
        required={required}
        errorMessage={validationError}
      />
    </div>
  );
}

/**
 * Field types we don't need to display in this view of the document
 */
const skippedFieldTypes = new Set<FieldTypes>(['image', 'rectangle']);

export function AccessibleDocument({
  onlySelected = false,
  className,
}: {
  onlySelected?: boolean;
  className?: string;
}) {
  const intl = useIntl();
  const fields = useFieldsContext();
  const { validationErrors, requestTitle, selectField, attachmentSlots } =
    useSignerContext();
  const { selectedFieldIds } = React.useContext(signatureRequestContext);
  const touchedFieldIds = useTouchedFieldIds();

  const shouldShowAttachmentsField =
    attachmentSlots.length > 0 && !onlySelected;

  const groupedFields = React.useMemo(() => {
    return fields.reduce(
      (groups, f) => {
        if ('group' in f && f.group != null) {
          groups[f.group] = groups[f.group] || [];
          groups[f.group].push(f);
        }
        return groups;
      },
      {} as Record<string, Field[]>,
    );
  }, [fields]);

  const skipGroupedField = (field: Field) => {
    if ('group' in field && field.group != null) {
      return groupedFields[field.group][0] !== field;
    }
    return false;
  };

  const shouldShowField = (f: Field) => {
    let show = true;
    if (onlySelected) {
      show = false;
      if (selectedFieldIds.includes(f.id)) {
        show = true;
      } else if ('group' in f && f.group != null) {
        // If the field is part of a group, check if any field in the group is selected
        show = Boolean(
          groupedFields[f.group]?.some((field) =>
            selectedFieldIds.includes(field.id),
          ),
        );
      }
    }
    return show;
  };

  return (
    <WhiteLabelThemeProvider mode="bright" colors="reset">
      <div
        className={classNames(styles.doc, className, {
          [styles.onlySelected]: onlySelected,
        })}
        onBlur={(evt) => {
          // When an element loses focus, we want to count it as "touched" so
          // we know when to show validation. An element is touched once it
          // has been selected and then deselected. This is a quick-and-dirty way
          // to deselect the field when the user interacts outside of one of the
          // inputs within this element container. The `evt.relatedTarget` on
          // a blur event is the element that is receiving focus. If it is `null`,
          // then that's a clear signal that we should deselect any active field.
          // This won't catch all cases, for example, interacting with the toolbar,
          // but it will catch most cases.
          if (!evt.relatedTarget && !onlySelected) {
            // When only showing the selected field, it is very disruptive to yank
            // the field away on blur.
            selectField(null);
          }
        }}
        data-testid={onlySelected ? undefined : 'form-view-container'}
      >
        {!onlySelected && (
          <Split gap="Micro Small" direction="vertical">
            <Split.Item>
              <Title>{requestTitle}</Title>
            </Split.Item>

            <Split.Item>
              <Text color="subtle">
                {intl.formatMessage(messages.formViewDescription)}
              </Text>
            </Split.Item>
          </Split>
        )}

        {fields
          .filter(
            (f) =>
              shouldShowField(f) &&
              !f.readOnly &&
              !f.hidden &&
              !skipGroupedField(f) &&
              !skippedFieldTypes.has(f.type),
          )
          .map((field) => {
            // type-narrow to types that can be grouped
            if (
              'group' in field &&
              // Make sure we have a group
              field.group != null
            ) {
              const { group } = field;
              const invalidField = groupedFields[group].find((field) => {
                return validationErrors[field.id];
              });
              const validationError =
                invalidField && touchedFieldIds.has(invalidField.id)
                  ? validationErrors[invalidField.id].message
                  : null;

              let validationInfo;
              let groupRequired = false;

              if (field.requirement) {
                if (field.type === 'checkbox') {
                  validationInfo = getGroupValidationInfo(
                    field.requirement,
                    intl,
                  );
                }
                if (field.type === 'checkbox' || field.type === 'radiobutton') {
                  groupRequired = isGroupRequired(field.requirement);
                }
              }

              return (
                <FieldGroup
                  key={field.id}
                  group={groupedFields[group]}
                  groupId={group}
                  required={groupRequired}
                  label={field.groupLabel ?? undefined}
                  validationError={validationError ?? undefined}
                  validationInfo={validationInfo}
                />
              );
            } else {
              return (
                <FormRow key={field.id}>
                  <FieldLabel field={field} />
                  <FieldInput field={field} />
                  <FieldHelperText
                    id={field.id}
                    required={!!field.required}
                    isDateField={field.type === 'date'}
                    errorMessage={
                      touchedFieldIds.has(field.id)
                        ? validationErrors[field.id]?.message
                        : undefined
                    }
                  />
                </FormRow>
              );
            }
          })}
        {shouldShowAttachmentsField && <AttachmentsField />}
      </div>
    </WhiteLabelThemeProvider>
  );
}
